import { useCallback, useEffect, useRef } from 'react'

/**
 * Scroll an element into view after a short delay, from a component that may
 * unmount first (docs/tool-scroll-timer-fix-scope.md).
 *
 * The tool pages used to fire and forget `setTimeout(() => document
 * .getElementById(id)?.scrollIntoView(...), delay)` from their submit
 * handlers. Nothing cleared that timer, so a test ending inside the delay
 * unmounted the page, Vitest tore down jsdom, and the timer then ran against a
 * missing `document`: an unhandled ReferenceError that failed CI at random
 * from 9 October 2026. Every pending timer is now owned by the component and
 * cleared when it unmounts, and a call that arrives after unmount (an async
 * handler finishing late) schedules nothing.
 *
 * The delay gives the result section time to render before the scroll, as
 * the inline timers did. Each call schedules its own scroll, so two quick
 * submissions still scroll twice. Motion follows the site's CSS, so users
 * who ask for reduced motion are not animated (Qodo, PR #412).
 */
export function useScrollIntoView() {
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>())
  const mounted = useRef(false)

  useEffect(() => {
    // Set here, not at render, so Strict Mode's mount, cleanup, mount replay
    // re-arms the hook after the first cleanup.
    mounted.current = true
    const pending = timers.current
    return () => {
      mounted.current = false
      pending.forEach(clearTimeout)
      pending.clear()
    }
  }, [])

  return useCallback((id: string, delayMs = 100) => {
    if (!mounted.current) return
    const timer = setTimeout(() => {
      timers.current.delete(timer)
      // 'auto' follows the page's CSS: globals.css makes <html> smooth and its
      // reduced-motion block (the site's one kill switch) sets it back to
      // auto, so reduced-motion users get an instant jump instead.
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: 'auto', block: 'start' })
    }, delayMs)
    timers.current.add(timer)
  }, [])
}
